import React, { useEffect, useState } from "react";
import axios from "axios";
import { endpoints } from "../apiConfig";
import { colors, fonts, space, radius, buttonPrimary, buttonSecondary, inputBase, pageHeading } from "../theme";

const SECTIONS = [
  { key: "revenue", label: "Collected Revenue" },
  { key: "growth", label: "Tenant Growth" },
  { key: "subscriptions", label: "Active vs Expired Subscriptions" },
  { key: "order_volume", label: "Order Volume" },
  { key: "most_active", label: "Most Active Tenants" },
  { key: "tenant_list", label: "Full Tenant List" },
];

export default function AdminDashboard() {
  const token = () => localStorage.getItem("admin_token");
  const authHeader = () => ({ Authorization: `Bearer ${token()}` });

  const [startDate, setStartDate] = useState(new Date(Date.now() - 180 * 86400000).toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [rankBy, setRankBy] = useState("count");
  const [report, setReport] = useState(null);
  const [checkedSections, setCheckedSections] = useState(SECTIONS.map((s) => s.key));

  const load = () => {
    if (!token()) {
      window.location.href = "/admin/login";
      return;
    }
    axios.get(endpoints.adminDashboardReport, {
      headers: authHeader(),
      params: { start_date: startDate, end_date: endDate, rank_by: rankBy },
    }).then((res) => setReport(res.data));
  };

  useEffect(load, []); // eslint-disable-line

  const toggleSection = (key) => {
    setCheckedSections((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const exportCsv = () => {
    const params = new URLSearchParams({
      start_date: startDate, end_date: endDate, rank_by: rankBy,
      sections: checkedSections.join(","), export: "1",
    });
    // Export streams a file, so it's opened directly rather than via axios —
    // the admin token needs to travel as a query param for this one request.
    window.open(`${endpoints.adminDashboardReport}?${params.toString()}&auth=${token()}`, "_blank");
  };

  return (
    <div className="admin-page" style={{ padding: space[6], background: colors.bgPage, minHeight: "100vh", fontFamily: fonts.body }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: space[3] }}>
        <h1 style={pageHeading}>Operations Dashboard</h1>
        <a href="/admin" style={buttonSecondary}>← Back to Tenants</a>
      </div>

      <div style={{ display: "flex", gap: space[3], alignItems: "flex-end", marginTop: space[4], marginBottom: space[5], flexWrap: "wrap" }}>
        <div>
          <label style={styles.label}>Start Date</label>
          <input type="date" style={inputBase} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div>
          <label style={styles.label}>End Date</label>
          <input type="date" style={inputBase} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
        <div>
          <label style={styles.label}>Rank Most-Active By</label>
          <select style={inputBase} value={rankBy} onChange={(e) => setRankBy(e.target.value)}>
            <option value="count">Order Count</option>
            <option value="value">Order $ Value</option>
          </select>
        </div>
        <button style={buttonPrimary} onClick={load}>Apply</button>
      </div>

      {!report ? (
        <p>Loading…</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: space[4] }}>
          {report.revenue && (
            <Card title="Collected Revenue">
              <BigNumber value={`$${report.revenue.collected_total.toFixed(2)}`} sub={`${report.revenue.collected_count} payment(s) marked paid`} />
            </Card>
          )}

          {report.subscriptions && (
            <Card title="Active vs Expired Subscriptions">
              <div style={{ display: "flex", gap: space[5] }}>
                <BigNumber value={report.subscriptions.active} sub="Active" />
                <BigNumber value={report.subscriptions.expired} sub="Expired" />
                <BigNumber value={report.subscriptions.free} sub="Free" />
              </div>
            </Card>
          )}

          {report.order_volume && (
            <Card title="Order Volume">
              <div style={{ display: "flex", gap: space[5] }}>
                <BigNumber value={report.order_volume.count} sub="Orders" />
                <BigNumber value={`$${report.order_volume.total.toFixed(2)}`} sub="Total Value" />
              </div>
            </Card>
          )}

          {report.growth && (
            <Card title="Tenant Growth (Free vs Subscribed)">
              <table style={styles.table}>
                <thead>
                  <tr><th style={styles.th}>Month</th><th style={styles.th}>Free</th><th style={styles.th}>Subscribed</th></tr>
                </thead>
                <tbody>
                  {report.growth.map((row) => (
                    <tr key={row.month}>
                      <td style={styles.td}>{row.month}</td>
                      <td style={styles.td}>{row.free_count}</td>
                      <td style={styles.td}>{row.subscribed_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {report.most_active && (
            <Card title={`Most Active Tenants (by ${rankBy === "value" ? "Order $ Value" : "Order Count"})`}>
              <table style={styles.table}>
                <thead>
                  <tr><th style={styles.th}>Business</th><th style={styles.th}>Orders</th><th style={styles.th}>Value</th></tr>
                </thead>
                <tbody>
                  {report.most_active.map((row, i) => (
                    <tr key={i}>
                      <td style={styles.td}>{row.business_name}</td>
                      <td style={styles.td}>{row.order_count}</td>
                      <td style={styles.td}>${row.order_value.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {report.tenant_list && (
            <Card title="Full Tenant List">
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Business</th><th style={styles.th}>Status</th>
                    <th style={styles.th}>Subscribed</th><th style={styles.th}>Amount</th>
                    <th style={styles.th}>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {report.tenant_list.map((row, i) => (
                    <tr key={i}>
                      <td style={styles.td}>{row.business_name}</td>
                      <td style={styles.td}>{row.status}</td>
                      <td style={styles.td}>{row.is_subscribed ? "Yes" : "Free"}</td>
                      <td style={styles.td}>${Number(row.subscription_amount).toFixed(2)}</td>
                      <td style={styles.td}>{new Date(row.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          <Card title="Export">
            <p style={{ fontFamily: fonts.body, fontSize: 12, color: colors.textMuted, marginTop: 0 }}>
              Choose which sections to include, then export as one combined CSV file covering the date range above.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: space[3] }}>
              {SECTIONS.map((s) => (
                <label key={s.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                  <input type="checkbox" checked={checkedSections.includes(s.key)} onChange={() => toggleSection(s.key)} />
                  {s.label}
                </label>
              ))}
            </div>
            <button style={buttonPrimary} onClick={exportCsv} disabled={checkedSections.length === 0}>
              Export CSV
            </button>
          </Card>
        </div>
      )}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ background: colors.surface, borderRadius: radius.md, border: `1px solid ${colors.border}`, padding: space[4] }}>
      <h2 style={{ fontFamily: fonts.display, fontSize: 15, margin: "0 0 12px" }}>{title}</h2>
      {children}
    </div>
  );
}

function BigNumber({ value, sub }) {
  return (
    <div>
      <div style={{ fontFamily: fonts.display, fontSize: 28, fontWeight: 700, color: colors.textPrimary }}>{value}</div>
      <div style={{ fontFamily: fonts.body, fontSize: 12, color: colors.textMuted }}>{sub}</div>
    </div>
  );
}

const styles = {
  label: { display: "block", fontSize: 11, color: colors.textSecondary, marginBottom: 4 },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", padding: "6px 8px", fontSize: 12, color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` },
  td: { padding: "6px 8px", fontSize: 13, color: colors.textPrimary, borderBottom: `1px solid ${colors.border}` },
};
